import { useEffect, useState } from 'react';
import { Box, ButtonBase } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { IconChevronDown, IconShieldCheck, IconCheck } from '@tabler/icons-react';

// "Why private?" nav popover. Self-contained: a text trigger with a caret that
// rotates when open, plus a popover anchored directly below. A transparent
// full-page backdrop closes it on outside click. Theme-native throughout.
const ROWS = [
  { lead: 'Never sold, never shared', rest: ' - not with advertisers, not with anyone.' },
  { lead: 'Disclosed only under a valid court order', rest: ' - never voluntarily.' },
  { lead: "That's the whole policy.", rest: ' No asterisks, no fine print.' },
];

const WhyPrivatePopover = () => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);

  // Escape closes the popover (standard dialog behavior; non-destructive).
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <Box sx={{ position: 'relative' }}>
      <ButtonBase
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="dialog"
        disableRipple
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          fontSize: 14,
          fontWeight: 500,
          color: open ? theme.palette.text.primary : theme.palette.text.dim,
          transition: `color ${theme.ds.motion.fast}`,
          '&:hover': { color: theme.palette.text.primary },
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
        }}
      >
        Why private?
        <Box
          aria-hidden
          sx={{
            display: 'inline-flex',
            transition: `transform ${theme.ds.motion.base}`,
            transform: open ? 'rotate(180deg)' : 'none',
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          }}
        >
          <IconChevronDown size={16} />
        </Box>
      </ButtonBase>

      {open && (
        <>
          <Box
            data-testid="why-private-backdrop"
            aria-hidden
            onClick={() => setOpen(false)}
            sx={{ position: 'fixed', inset: 0, zIndex: 1 }}
          />
          <Box
            role="dialog"
            aria-label="Private by design"
            sx={{
              position: 'absolute',
              top: 'calc(100% + 14px)',
              left: 0,
              zIndex: 2,
              width: 312,
              p: '18px',
              bgcolor: theme.palette.surface.raised,
              border: `1px solid ${theme.palette.overlay.border}`,
              borderRadius: '14px',
              boxShadow: theme.palette.overlay.shadow,
              backgroundImage: 'none',
            }}
          >
            <Box
              aria-hidden
              sx={{
                position: 'absolute',
                top: -6,
                left: 24,
                width: 12,
                height: 12,
                bgcolor: theme.palette.surface.raised,
                borderLeft: `1px solid ${theme.palette.divider}`,
                borderTop: `1px solid ${theme.palette.divider}`,
                transform: 'rotate(45deg)',
              }}
            />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5 }}>
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: '8px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: alpha(theme.palette.success.main, 0.13),
                  color: theme.palette.success.main,
                }}
              >
                <IconShieldCheck size={18} />
              </Box>
              <Box component="span" sx={{ fontWeight: 600, fontSize: 15, color: theme.palette.text.primary }}>
                Private by design
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              {ROWS.map((r) => (
                <Box key={r.lead} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Box aria-hidden sx={{ flexShrink: 0, mt: '2px', color: theme.palette.success.main, display: 'inline-flex' }}>
                    <IconCheck size={16} />
                  </Box>
                  <Box component="span" sx={{ fontSize: 13, lineHeight: 1.5, color: theme.palette.text.secondary }}>
                    <Box component="span" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                      {r.lead}
                    </Box>
                    {r.rest}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default WhyPrivatePopover;
